import { useEffect } from 'react';
import { useMap } from '@vis.gl/react-google-maps';

export default function GeoJsonLayer({ features, onFeatureSelect, editable = false, teamColors = {}, isPinningMode = false, onMapClick }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // Clear existing features
    map.data.forEach((feature) => {
      map.data.remove(feature);
    });

    let timeoutId;
    if (features && features.length > 0) {
      let currentIndex = 0;
      const batchSize = 10;
      
      const renderBatch = () => {
        const batch = features.slice(currentIndex, currentIndex + batchSize);
        if (batch.length === 0) return;
        
        map.data.addGeoJson({
          type: 'FeatureCollection',
          features: batch
        });
        
        currentIndex += batchSize;
        if (currentIndex < features.length) {
          timeoutId = setTimeout(renderBatch, 30);
        }
      };
      
      renderBatch();

      // Style features
      map.data.setStyle((feature) => {
        const isSelected = !!feature.getProperty('selected');
        const teamId = feature.getProperty('team_id');
        const baseColor = teamColors[teamId] || '#eb0a1e'; // Default to Toyota Red if no team color
        
        return {
          fillColor: baseColor,
          fillOpacity: isSelected ? 0.6 : 0.3,
          strokeColor: baseColor,
          strokeWeight: isSelected ? 3 : 1.5,
          clickable: true,
          editable: isSelected && editable,
        };
      });

      // Handle clicks
      const clickListener = map.data.addListener('click', (event) => {
        if (isPinningMode) {
          if (onMapClick) onMapClick({ latLng: event.latLng, detail: { latLng: event.latLng } });
          return;
        }

        // Deselect all
        map.data.forEach((f) => f.setProperty('selected', false));
        // Select clicked
        event.feature.setProperty('selected', true);
        
        if (onFeatureSelect) {
          onFeatureSelect(event.feature);
        }
      });

      // Handle right clicks for deleting points
      const rightClickListener = map.data.addListener('rightclick', (event) => {
        if (!editable) return;
        const feature = event.feature;
        if (!feature || !feature.getProperty('selected')) return;

        const geometry = feature.getGeometry();
        if (geometry && geometry.getType() === 'Polygon') {
          const latLng = event.latLng;
          let minDistance = Infinity;
          let targetRing = -1;
          let targetVertex = -1;

          const rings = geometry.getArray();
          rings.forEach((ring, rIndex) => {
            const vertices = ring.getArray();
            vertices.forEach((v, vIndex) => {
              const dLat = v.lat() - latLng.lat();
              const dLng = v.lng() - latLng.lng();
              const distSq = dLat * dLat + dLng * dLng;
              if (distSq < minDistance) {
                minDistance = distSq;
                targetRing = rIndex;
                targetVertex = vIndex;
              }
            });
          });

          // Threshold for click: approx 10-20 meters
          if (minDistance < 0.0000005) {
            const newRings = rings.map((ring, rIndex) => {
              if (rIndex === targetRing) {
                const arr = ring.getArray();
                if (arr.length <= 3) return arr; // Don't allow less than a line
                arr.splice(targetVertex, 1);
                
                // Keep the polygon closed
                if (targetVertex === 0) {
                   arr[arr.length - 1] = arr[0];
                } else if (targetVertex === arr.length) {
                   arr.push(arr[0]);
                }
                return arr;
              }
              return ring.getArray();
            });
            feature.setGeometry(new window.google.maps.Data.Polygon(newRings));
          }
        }
      });

      return () => {
        window.google.maps.event.removeListener(clickListener);
        window.google.maps.event.removeListener(rightClickListener);
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [map, features, onFeatureSelect]);

  return null;
}
